import { createSlice } from '@reduxjs/toolkit';
import ApiClientConnector from 'src/api/ApiClientConnector';
import { calculateTotalServiceTypeCost, transformToServiceTypeDetails } from '../../actions/services/ServiceTypeDetailSliceActions';

const initialState = {
    showServiceDetails: false,
    serviceTypeDetails: [],
    serviceCost: 0,
    provider: null,
};
export const ServiceTypeDetailSlice = createSlice({
    name: 'serviceTypeDetails',
    initialState,
    reducers: {
        setServiceTypeDetailsVisibility(state, action) {
            state.showServiceDetails = action.payload;
        },
        setServiceTypeDetails: (state, action) => {
            state.serviceTypeDetails = action.payload;
        },
        updateServiceSupplementSelection(state, action) {
            const { serviceUnitRequestId, serviceUnitTypeId, supplementReferenceId, isSelected } = action.payload;

            const updatedServiceUnitRequests = state.serviceTypeDetails?.ServiceUnitRequestResults?.map((unitRequest) => {
                if (unitRequest.serviceUnitRequestId !== serviceUnitRequestId) return unitRequest;

                const updatedServiceUnits = unitRequest.serviceUnits.map((unit) => {
                    if (unit.ServiceUnitTypeId === serviceUnitTypeId) {
                        const updatedUnit = { ...unit };

                        if (updatedUnit.SupplementResults) {
                            updatedUnit.SupplementResults = updatedUnit.SupplementResults.map(supplement => {
                                if (supplement.ReferenceId === supplementReferenceId) {
                                    return { ...supplement, Selected: isSelected };
                                }
                                return supplement;
                            });
                        }

                        updatedUnit.TotalCost = calculateTotalServiceTypeCost(updatedUnit);
                        return updatedUnit;
                    }
                    return unit;
                });

                return {
                    ...unitRequest,
                    serviceUnits: updatedServiceUnits
                };
            });

            state.serviceTypeDetails.ServiceUnitRequestResults = updatedServiceUnitRequests;
            state.serviceCost = updatedServiceUnitRequests.flatMap(req => req.serviceUnits).reduce((total, unit) => total + (unit.TotalCost || 0), 0);
        },
        updateServiceDiscountSelection: (state, action) => {
            const { serviceUnitRequestId, serviceUnitTypeId, selectedSelectionId } = action.payload;

            const updatedServiceUnitRequests = state.serviceTypeDetails?.ServiceUnitRequestResults?.map((unitRequest) => {
                if (unitRequest.serviceUnitRequestId !== serviceUnitRequestId) return unitRequest;

                const updatedServiceUnits = unitRequest.serviceUnits.map((unit) => {
                    if (unit.ServiceUnitTypeId === serviceUnitTypeId) {
                        const updatedUnit = { ...unit };

                        if (updatedUnit.DiscountResults) {
                            updatedUnit.DiscountResults = updatedUnit.DiscountResults.map(discount => ({
                                ...discount,
                                UserIncluded: discount.SelectionId === selectedSelectionId,

                            }));
                        }

                        updatedUnit.TotalCost = calculateTotalServiceTypeCost(updatedUnit);
                        return updatedUnit;
                    }
                    return unit;
                });

                return {
                    ...unitRequest,
                    serviceUnits: updatedServiceUnits
                };
            });

            state.serviceTypeDetails.ServiceUnitRequestResults = updatedServiceUnitRequests;
            state.serviceCost = updatedServiceUnitRequests.flatMap(req => req.serviceUnits).reduce((total, unit) => total + (unit.TotalCost || 0), 0);
        },
        updateServiceUnitSelection: (state, action) => {
            const { serviceUnitRequestId, serviceUnitTypeId } = action.payload;

            const serviceUnitRequests = state.serviceTypeDetails?.ServiceUnitRequestResults?.map((unitRequest) => {
                const updatedServiceUnits = unitRequest.serviceUnits.map((unit) => ({
                    ...unit,
                    IsUnitSelected: false,
                    TotalCost: 0
                }));

                return {
                    ...unitRequest,
                    serviceUnits: updatedServiceUnits
                };
            });

            const updatedServiceUnitRequests = serviceUnitRequests?.map((unitRequest) => {
                if (unitRequest.serviceUnitRequestId !== serviceUnitRequestId) return unitRequest;

                const updatedServiceUnits = unitRequest.serviceUnits.map((unit) => {
                    if (unit.ServiceUnitTypeId === serviceUnitTypeId) {
                        return {
                            ...unit,
                            IsUnitSelected: true,
                            TotalCost: calculateTotalServiceTypeCost(unit)
                        };
                    }
                    return unit;
                });

                return {
                    ...unitRequest,
                    serviceUnits: updatedServiceUnits
                };
            });

            state.serviceTypeDetails.ServiceUnitRequestResults = updatedServiceUnitRequests;
            state.serviceCost = updatedServiceUnitRequests.flatMap(req => req.serviceUnits).reduce((total, unit) => total + (unit.TotalCost || 0), 0);
        },

        clearServiceTypeDetails: (state) => {
            if (state.serviceTypeDetails?.ServiceUnitRequestResults) {
                // Only clear unit selections, keep service info intact
                state.serviceTypeDetails.ServiceUnitRequestResults = state.serviceTypeDetails.ServiceUnitRequestResults.map(unitRequest => ({
                    ...unitRequest,
                    serviceUnits: unitRequest.serviceUnits.map(unit => ({
                        ...unit,
                        IsUnitSelected: false,
                        TotalCost: 0
                    }))
                }));
            }
            state.serviceCost = 0;
            state.showServiceDetails = true;
        },
        clearServiceTypeDetailsFromSearchPage: (state) => {
            if (state.serviceTypeDetails?.ServiceUnitRequestResults) {
                state.serviceTypeDetails.ServiceUnitRequestResults = state.serviceTypeDetails.ServiceUnitRequestResults.map(unitRequest => ({
                    ...unitRequest,
                    serviceUnits: unitRequest.serviceUnits.map(unit => ({
                        ...unit,
                        IsUnitSelected: false,
                        TotalCost: 0
                    }))
                }));
            }
            state.serviceTypeDetails = null;
            state.serviceCost = 0;
            state.showServiceDetails = false;
        },
        setProvider: (state, action) => {
            state.provider = action.payload;
        },

    },
});

export const {
    setServiceTypeDetailsVisibility,
    setServiceTypeDetails,
    updateServiceSupplementSelection,
    updateServiceDiscountSelection,
    updateServiceUnitSelection,
    clearServiceTypeDetails,
    setProvider,
    clearServiceTypeDetailsFromSearchPage
} = ServiceTypeDetailSlice.actions;

export const fetchServiceTypeDetails =
    (instance, accounts, serviceId, serviceContractId, criteria, services) => async (dispatch) => {
        const client = await ApiClientConnector(instance, accounts);
        const response = await client.services.postById(serviceId, {
            CheckIn: criteria.CheckIn,
            CheckOut: criteria.CheckOut,
            Honeymoon: criteria.Honeymoon,
            Wedding: criteria.Wedding,
            Units: criteria.Units,
            ServiceContractId: serviceContractId
        });

        const serviceTypes = response.Result || {};
        const serviceTypeDetails = transformToServiceTypeDetails(serviceTypes, services);
        dispatch(setServiceTypeDetails(serviceTypeDetails));
    };


export default ServiceTypeDetailSlice.reducer;